import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FolderOpen,
  Calendar,
  Clock,
  Bell,
  CheckCircle2,
  ShieldCheck,
  ArrowRight,
  Zap,
  Search,
  AlertCircle,
  FileText,
  ChevronDown,
  Check,
  Users,
  Sparkles,
  Database,
  Landmark,
  Scale,
  RefreshCw,
  ExternalLink,
  Shield,
  FileCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";

interface ProcessSimulation {
  radicado: string;
  autoridad: string;
  claseProceso: string;
  demandante: string;
  demandado: string;
  ultimaActuacion: string;
  fechaActuacion: string;
  estadoTipo: string;
  terminoDias: number;
  tipoTermino: string;
  fechaVencimiento: string;
  sistema: "CPNU" | "SAMAI" | "SIUGJ" | "TYBA";
}

const PRESET_PROCESSES: ProcessSimulation[] = [
  {
    radicado: "11001-31-03-014-2023-00482-00",
    autoridad: "Juzgado 14 Civil del Circuito de Bogotá",
    claseProceso: "Ejecutivo Singular de Mayor Cuantía",
    demandante: "Inversiones del Norte S.A.S.",
    demandado: "Constructora Andina Ltda.",
    ultimaActuacion: "Auto decreta medidas cautelares y corre traslado de excepciones",
    fechaActuacion: "Hoy, 06:14 AM (Publicación de Estados)",
    estadoTipo: "Estado Electrónico No. 042",
    terminoDias: 3,
    tipoTermino: "Traslado de Excepciones Previas (Art. 110 CGP)",
    fechaVencimiento: "En 3 días hábiles (Viernes 5:00 PM)",
    sistema: "CPNU",
  },
  {
    radicado: "05001-33-33-022-2022-00195-00",
    autoridad: "Tribunal Administrativo de Antioquia - Despacho 03",
    claseProceso: "Nulidad y Restablecimiento del Derecho",
    demandante: "Carlos Alberto Montoya",
    demandado: "Municipio de Medellín - Secretaría de Hacienda",
    ultimaActuacion: "Auto fija fecha para Audiencia Inicial (Art. 180 CPACA)",
    fechaActuacion: "Ayer, 05:40 PM",
    estadoTipo: "Aviso SAMAI",
    terminoDias: 10,
    tipoTermino: "Preparación de Alegatos de Apertura",
    fechaVencimiento: "En 10 días hábiles",
    sistema: "SAMAI",
  },
  {
    radicado: "76001-31-05-008-2024-00112-00",
    autoridad: "Juzgado 08 Laboral del Circuito de Cali",
    claseProceso: "Ordinario Laboral de Primera Instancia",
    demandante: "María Elena Restrepo",
    demandado: "Logística y Distribuciones del Valle S.A.",
    ultimaActuacion: "Auto corre traslado de la contestación y excepciones de mérito",
    fechaActuacion: "Hoy, 06:30 AM",
    estadoTipo: "Estado SIUGJ No. 018",
    terminoDias: 5,
    tipoTermino: "Descorrer Traslado (Art. 392 CGP / CPTSS)",
    fechaVencimiento: "En 5 días hábiles",
    sistema: "SIUGJ",
  },
];

export default function ControlExpedientesLanding() {
  const navigate = useNavigate();
  const [selectedProcess, setSelectedProcess] = useState<ProcessSimulation>(PRESET_PROCESSES[0]);
  const [customRadicado, setCustomRadicado] = useState<string>("");
  const [isSimulating, setIsSimulating] = useState<boolean>(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const handleSimulateCustom = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customRadicado.trim()) return;

    setIsSimulating(true);
    setTimeout(() => {
      setSelectedProcess({
        radicado: customRadicado.trim().replace(/\s+/g, ""),
        autoridad: "Juzgado Civil del Circuito (Monitoreo Activo)",
        claseProceso: "Proceso Judicial Vinculado",
        demandante: "Tu Firma Jurídica / Apoderado",
        demandado: "Contraparte Registrada",
        ultimaActuacion: "Sincronización confirmada: Actuación reciente detectada en estados electrónicos",
        fechaActuacion: "Actualizado hoy, 06:00 AM",
        estadoTipo: "Estado Electrónico Automatizado",
        terminoDias: 3,
        tipoTermino: "Cómputo Automático de Término Hábil",
        fechaVencimiento: "3 días hábiles restantes (descontando festivos y vacancia)",
        sistema: "CPNU",
      });
      setIsSimulating(false);
    }, 600);
  };

  const faqs = [
    {
      q: "¿Cómo obtiene el sistema la información de los juzgados?",
      a: "Nuestros agentes se conectan directamente a las fuentes oficiales de la Rama Judicial de Colombia: el portal CPNU (Justicia XXI Web), el sistema SAMAI del Consejo de Estado y Tribunales Administrativos, el nuevo SIUGJ y el sistema TYBA. Las consultas se ejecutan de manera desatendida todas las mañanas antes de las 6:30 AM.",
    },
    {
      q: "¿Cómo se calculan los términos procesales y vacancias?",
      a: "El motor de cálculo computa los términos en días hábiles procesales, aplicando estrictamente las reglas del Código General del Proceso (CGP - Ley 1564 de 2012, arts. 110 y 118) y del CPACA. Descuenta automáticamente fines de semana, días cívicos, festivos nacionales y los periodos oficiales de vacancia judicial acordados por el Consejo Superior de la Judicatura.",
    },
    {
      q: "¿Puedo recibir alertas por WhatsApp y correo electrónico?",
      a: "Sí. Cada vez que un despacho publica un nuevo auto, fijación en lista, estado o traslado sobre cualquiera de tus radicados vigilados, recibes una notificación instantánea detallada con el texto de la actuación, la fecha de notificación y la alerta del término que empieza a correr.",
    },
    {
      q: "¿Es legal y válido este monitoreo en Colombia?",
      a: "Completamente. Cumple plenamente con la Ley 2213 de 2022 (uso de las TIC en la Rama Judicial), los principios de publicidad de las providencias y el régimen de protección de datos de la Ley 1581 de 2012. Toda la información consultada proviene de fuentes públicas oficiales de la Rama Judicial.",
    },
    {
      q: "¿Puedo vincular clientes para que vean el estado de su caso?",
      a: "Sí. El módulo de Despachos te permite crear perfiles de clientes y asignarles sus respectivos procesos, permitiéndoles consultar sus avances o recibir reportes ejecutivos en PDF sin sobrecargar las líneas telefónicas de tu despacho.",
    },
    {
      q: "¿Cuántos procesos puedo vigilar en mi despacho?",
      a: "Dependiendo de tu plan, puedes vigilar desde 10 procesos en planes individuales hasta más de 500 procesos con sincronización multiabogado para firmas medianas y departamentos jurídicos empresariales.",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      <SEO
        title="Control de Expedientes Judiciales en Colombia - CPNU, SAMAI, SIUGJ & TYBA | Mi Asistente Legal"
        description="Vigilancia y monitoreo inteligente de procesos judiciales en Colombia. Sincronización automática a las 6 AM de autos, estados electrónicos y cálculo de términos hábiles sin riesgo de caducidad."
        keywords="control de expedientes, vigilancia judicial, rama judicial colombia, cpnu consulta procesos, samai estados, siugj autos, tyba bogota, terminos procesales cgp, software para abogados colombia"
      />

      <Header />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative pt-16 pb-20 lg:pt-24 lg:pb-32 overflow-hidden bg-gradient-to-b from-blue-50/70 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 border-b border-slate-200/80 dark:border-slate-800">
          {/* Ambient light glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[450px] bg-gradient-to-tr from-blue-500/15 via-indigo-500/10 to-teal-500/10 rounded-full blur-[130px] pointer-events-none" />

          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            {/* Pill Badge */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-100/80 dark:bg-blue-950/80 border border-blue-300/80 dark:border-blue-800 text-blue-800 dark:text-blue-300 text-xs sm:text-sm font-extrabold tracking-wide uppercase shadow-sm">
                <ShieldCheck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span>Vigilancia Judicial Automatizada • CPNU, SAMAI, SIUGJ & TYBA</span>
              </div>
            </div>

            {/* Title & Tagline */}
            <div className="text-center max-w-4xl mx-auto mb-10">
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.12] mb-6">
                Control y Monitoreo Inteligente de{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-teal-600 dark:from-blue-400 dark:via-indigo-300 dark:to-teal-300">
                  Expedientes Judiciales
                </span>{" "}
                en Colombia
              </h1>

              <p className="text-base sm:text-xl text-slate-600 dark:text-slate-300 leading-relaxed max-w-2xl mx-auto">
                Rastreo desatendido de tus radicados de 23 dígitos. Detecta autos, estados electrónicos y traslados
                cada mañana a las 6:00 AM, con cómputo automático de términos en días hábiles procesales.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
              <Button
                size="lg"
                onClick={() => navigate("/signup")}
                className="w-full sm:w-auto h-13 px-8 text-base font-extrabold bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-xl shadow-blue-600/25 hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center gap-2"
              >
                <span>Comenzar Prueba Gratuita (14 Días)</span>
                <ArrowRight className="w-5 h-5" />
              </Button>

              <Button
                size="lg"
                variant="outline"
                onClick={() => {
                  const demoEl = document.getElementById("simulador-expedientes");
                  if (demoEl) demoEl.scrollIntoView({ behavior: "smooth" });
                }}
                className="w-full sm:w-auto h-13 px-7 text-base font-bold border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"
              >
                <Search className="w-4 h-4 mr-2 text-blue-600" />
                <span>Ver Simulador en Vivo</span>
              </Button>
            </div>

            {/* Trust Metrics Bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto pt-6 border-t border-slate-200/80 dark:border-slate-800/80">
              <div className="text-center p-3">
                <div className="text-2xl sm:text-3xl font-black text-blue-600 dark:text-blue-400">06:00 AM</div>
                <div className="text-xs sm:text-sm font-semibold text-slate-600 dark:text-slate-400 mt-0.5">
                  Sincronización matutina diaria
                </div>
              </div>
              <div className="text-center p-3">
                <div className="text-2xl sm:text-3xl font-black text-indigo-600 dark:text-indigo-400">100%</div>
                <div className="text-xs sm:text-sm font-semibold text-slate-600 dark:text-slate-400 mt-0.5">
                  Días hábiles y vacancias CSJ
                </div>
              </div>
              <div className="text-center p-3">
                <div className="text-2xl sm:text-3xl font-black text-teal-600 dark:text-teal-400">4 Redes</div>
                <div className="text-xs sm:text-sm font-semibold text-slate-600 dark:text-slate-400 mt-0.5">
                  CPNU, SAMAI, SIUGJ y TYBA
                </div>
              </div>
              <div className="text-center p-3">
                <div className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400">0 Términos</div>
                <div className="text-xs sm:text-sm font-semibold text-slate-600 dark:text-slate-400 mt-0.5">
                  Vencidos o inadvertidos
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Interactive Simulator Section */}
        <section id="simulador-expedientes" className="py-20 bg-white dark:bg-slate-900 border-b border-slate-200/80 dark:border-slate-800 scroll-mt-20">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 font-bold mb-3">
                Simulador en Tiempo Real
              </Badge>
              <h2 className="text-2xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
                Así monitorea SARAI tus procesos en la Rama Judicial
              </h2>
              <p className="text-slate-600 dark:text-slate-300 text-sm sm:text-base mt-2">
                Selecciona un proceso judicial de prueba o escribe un radicado para ver cómo se extraen las
                actuaciones, la autoridad y el conteo de términos.
              </p>
            </div>

            {/* Presets and Custom Input */}
            <div className="max-w-4xl mx-auto mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                {PRESET_PROCESSES.map((proc, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedProcess(proc)}
                    className={`p-3.5 rounded-xl border text-left transition-all ${
                      selectedProcess.radicado === proc.radicado
                        ? "border-blue-600 bg-blue-50/70 dark:bg-blue-950/40 shadow-md ring-2 ring-blue-600/20"
                        : "border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                        {proc.sistema}
                      </span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                        {proc.terminoDias} días hábiles
                      </span>
                    </div>
                    <div className="text-xs font-mono font-bold text-slate-900 dark:text-white truncate">
                      {proc.radicado}
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5">
                      {proc.claseProceso}
                    </div>
                  </button>
                ))}
              </div>

              {/* Quick Search Bar */}
              <form onSubmit={handleSimulateCustom} className="flex gap-2">
                <div className="relative flex-grow">
                  <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={customRadicado}
                    onChange={(e) => setCustomRadicado(e.target.value)}
                    placeholder="Escribe tu radicado de 23 dígitos (ej: 11001310301420230048200)..."
                    className="w-full h-12 pl-10 pr-4 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSimulating}
                  className="h-12 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shrink-0"
                >
                  {isSimulating ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <span>Simular Rastreo</span>
                  )}
                </Button>
              </form>
            </div>

            {/* Simulated Process Card */}
            <div className="max-w-4xl mx-auto bg-slate-50 dark:bg-slate-950/70 rounded-2xl border border-slate-200/90 dark:border-slate-800 shadow-xl p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-200 dark:border-slate-800">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-emerald-600 text-white font-bold text-xs">
                      {selectedProcess.sistema} • Sincronizado
                    </Badge>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      Última lectura: {selectedProcess.fechaActuacion}
                    </span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-mono font-black text-slate-900 dark:text-white">
                    {selectedProcess.radicado}
                  </h3>
                  <p className="text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400 mt-0.5">
                    {selectedProcess.autoridad}
                  </p>
                </div>

                <div className="text-right sm:border-l sm:pl-6 border-slate-200 dark:border-slate-800">
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Término Activo</div>
                  <div className="text-2xl font-black text-amber-600 dark:text-amber-400 flex items-center justify-end gap-1.5 mt-0.5">
                    <Clock className="w-5 h-5 text-amber-500 animate-pulse" />
                    <span>{selectedProcess.terminoDias} Días Hábiles</span>
                  </div>
                  <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                    {selectedProcess.fechaVencimiento}
                  </div>
                </div>
              </div>

              {/* Process Metadata Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200/70 dark:border-slate-800/80">
                  <span className="text-[11px] font-bold text-slate-400 uppercase">Partes Procesales</span>
                  <div className="mt-1 space-y-1 text-xs">
                    <div>
                      <span className="font-bold text-slate-700 dark:text-slate-300">Demandante:</span>{" "}
                      <span className="text-slate-600 dark:text-slate-400">{selectedProcess.demandante}</span>
                    </div>
                    <div>
                      <span className="font-bold text-slate-700 dark:text-slate-300">Demandado:</span>{" "}
                      <span className="text-slate-600 dark:text-slate-400">{selectedProcess.demandado}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200/70 dark:border-slate-800/80">
                  <span className="text-[11px] font-bold text-slate-400 uppercase">Tipo de Actuación & Estado</span>
                  <div className="mt-1 space-y-1 text-xs">
                    <div>
                      <span className="font-bold text-slate-700 dark:text-slate-300">Notificación:</span>{" "}
                      <span className="text-blue-600 dark:text-blue-400 font-semibold">{selectedProcess.estadoTipo}</span>
                    </div>
                    <div>
                      <span className="font-bold text-slate-700 dark:text-slate-300">Efecto Legal:</span>{" "}
                      <span className="text-slate-600 dark:text-slate-400">{selectedProcess.tipoTermino}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Latest Actuacion Detail */}
              <div className="p-4 rounded-xl bg-blue-50/80 dark:bg-blue-950/40 border border-blue-200/80 dark:border-blue-900/60 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                <div className="flex-grow">
                  <span className="text-xs font-bold text-blue-900 dark:text-blue-200 block">
                    Resumen Automático de la Providencia Detectada
                  </span>
                  <p className="text-xs text-blue-800 dark:text-blue-300 mt-0.5 leading-relaxed">
                    «{selectedProcess.ultimaActuacion}». El motor de inteligencia legal ha clasificado esta actuación como auto interlocutorio y ha programado la alerta preventiva de vencimiento en el calendario procesal del despacho.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pain vs Solution Comparison */}
        <section className="py-20 bg-slate-50 dark:bg-slate-950 border-b border-slate-200/80 dark:border-slate-800">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <Badge className="bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold mb-3">
                El Litigio Tradicional vs Mi Asistente Legal
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
                ¿Por qué los mejores despachos en Colombia automatizan su vigilancia judicial?
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* The Old Way */}
              <div className="p-8 rounded-2xl bg-white dark:bg-slate-900 border border-rose-200 dark:border-rose-950/80 shadow-md">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-950/70 text-rose-600 flex items-center justify-center font-bold">
                    ✕
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">Litigio Manual Tradicional</h3>
                </div>
                <ul className="space-y-4 text-sm text-slate-600 dark:text-slate-300">
                  <li className="flex items-start gap-2.5">
                    <span className="text-rose-500 font-bold mt-0.5">•</span>
                    <span>Revisar manualmente decenas de estados electrónicos en PDF y páginas lentas cada mañana.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-rose-500 font-bold mt-0.5">•</span>
                    <span>Riesgo permanente de vencimiento de términos, pérdida de traslados y caducidad de acciones.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-rose-500 font-bold mt-0.5">•</span>
                    <span>Hojas de Excel desactualizadas y carpetas en Google Drive sin sincronización judicial.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-rose-500 font-bold mt-0.5">•</span>
                    <span>Horas consumidas por dependientes judiciales solo refrescando páginas del CPNU y SAMAI.</span>
                  </li>
                </ul>
              </div>

              {/* The SARAI Way */}
              <div className="p-8 rounded-2xl bg-white dark:bg-slate-900 border border-emerald-300 dark:border-emerald-800 shadow-xl shadow-emerald-600/5">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-950/70 text-emerald-600 flex items-center justify-center font-bold">
                    ✓
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">Con Mi Asistente Legal (SARAI)</h3>
                </div>
                <ul className="space-y-4 text-sm text-slate-700 dark:text-slate-200">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span>Sincronización desatendida a las 6:00 AM de todas tus actuaciones y fijaciones en lista.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span>Cómputo exacto de términos en días hábiles procesales, descontando festivos y vacancia CSJ.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span>Alertas preventivas automáticas por WhatsApp y correo antes de que venza cualquier plazo.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span>Expediente digital centralizado con historial cronológico, clasificación IA y anexos seguros.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 6 Core Pillars Grid */}
        <section className="py-20 bg-white dark:bg-slate-900 border-b border-slate-200/80 dark:border-slate-800">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 font-bold mb-3">
                Arquitectura Integral del Módulo
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
                Todo lo que tu despacho necesita para controlar sus procesos
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {/* Feature 1 */}
              <div className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-950/70 text-blue-600 flex items-center justify-center mb-4">
                  <Database className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                  Cobertura Cuádruple Unificada
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  Conexión directa con CPNU (Justicia XXI Web), SAMAI (Consejo de Estado y Tribunales), SIUGJ y TYBA en una única bandeja consolidada.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-950/70 text-indigo-600 flex items-center justify-center mb-4">
                  <Calendar className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                  Cómputo Legal de Términos
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  Algoritmo adaptado al CGP y CPACA. Calcula días hábiles, plazos de traslado (Art. 110 CGP) y excluye Semana Santa, festivos y vacancia judicial.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-950/70 text-amber-600 flex items-center justify-center mb-4">
                  <Bell className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                  Alertas Preventivas Multicanal
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  Notificaciones prioritarias por correo y WhatsApp con recordatorios a los 5, 3 y 1 días antes de la fecha límite para descorrer recursos.
                </p>
              </div>

              {/* Feature 4 */}
              <div className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-teal-100 dark:bg-teal-950/70 text-teal-600 flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                  Clasificación IA de Providencias
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  El motor de IA analiza el texto de la providencia para identificar si es admisorio, mandamiento de pago, fijación de audiencia o sentencia.
                </p>
              </div>

              {/* Feature 5 */}
              <div className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-950/70 text-purple-600 flex items-center justify-center mb-4">
                  <Search className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                  Búsqueda por Cédula o Razón Social
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  Rastreo masivo no solo por radicado, sino por nombre de personas naturales o jurídicas para detectar procesos nuevos no notificados.
                </p>
              </div>

              {/* Feature 6 */}
              <div className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-950/70 text-emerald-600 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                  Gestión de Clientes y Cuadernos
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  Asigna expedientes a tus clientes, genera reportes de estado procesal en PDF en 1 clic y almacena memoriales y anexos con seguridad bancaria.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Legal Framework & Compliance */}
        <section className="py-16 bg-slate-50 dark:bg-slate-950 border-b border-slate-200/80 dark:border-slate-800">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <Scale className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                  Blindaje Jurídico y Validez Normativa en Colombia
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                <div>
                  <h4 className="font-bold text-slate-900 dark:text-white mb-1">Ley 2213 de 2022</h4>
                  <p>Vigencia permanente del uso de las tecnologías de la información en las actuaciones judiciales y notificaciones electrónicas.</p>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 dark:text-white mb-1">Código General del Proceso</h4>
                  <p>Apego riguroso a los arts. 110 (traslados), 118 (cómputo de términos) y 295 (notificación por estado electrónico).</p>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 dark:text-white mb-1">Ley 1581 de 2012</h4>
                  <p>Estricto cumplimiento de Hábeas Data y cifrado de datos procesales confidenciales de litigantes y clientes.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white dark:bg-slate-900 border-b border-slate-200/80 dark:border-slate-800">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold mb-3">
                Preguntas Frecuentes
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
                Todo lo que necesitas saber sobre el Control de Expedientes
              </h2>
            </div>

            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full text-left p-4 sm:p-5 flex items-center justify-between gap-4 font-bold text-sm sm:text-base text-slate-900 dark:text-white"
                  >
                    <span>{faq.q}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-200 ${
                        openFaq === idx ? "rotate-180 text-blue-600" : ""
                      }`}
                    />
                  </button>
                  {openFaq === idx && (
                    <div className="px-4 sm:px-5 pb-5 pt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed border-t border-slate-200/60 dark:border-slate-800/60">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final Conversion Banner */}
        <section className="py-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-teal-600 text-white relative overflow-hidden">
          <div className="container mx-auto px-4 sm:px-6 relative z-10 text-center max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-5xl font-black tracking-tight mb-4">
              Automatiza la vigilancia de tus procesos hoy mismo
            </h2>
            <p className="text-base sm:text-lg text-blue-100 mb-8 max-w-xl mx-auto">
              Únete a las firmas de abogados y departamentos jurídicos que nunca más dejaron vencer un término procesal.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                onClick={() => navigate("/signup")}
                className="w-full sm:w-auto h-13 px-8 text-base font-extrabold bg-white text-blue-900 hover:bg-blue-50 rounded-xl shadow-xl hover:scale-105 transition-all"
              >
                Comenzar Prueba Gratuita (14 Días)
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/request-demo")}
                className="w-full sm:w-auto h-13 px-7 text-base font-bold border-white/40 text-white hover:bg-white/10 rounded-xl"
              >
                Solicitar Demostración Guiada
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

